import { drillDiagrams, type DrillData } from '@coach/core';
import { Modal } from '@coach/ui';
import { lazy, Suspense, useEffect, useState } from 'react';
import { ZoomView } from './ZoomView';
import { sanitizeSvgString } from '../../lib/svgImport';

// The animator only loads when someone presses Watch, so the plan screen —
// which is in the main bundle — doesn't carry it.
const PlayAnimator = lazy(() => import('../playbook/PlayAnimator').then((m) => ({ default: m.PlayAnimator })));

/** Tab text for a diagram: its label, or its number when it has none. */
export const diagramTabLabel = (label: string, i: number) => label || `Diagram ${i + 1}`;

/** List-row hint: " · 📐" for a diagram, " · 📐 3" for several. */
export function diagramBadge(d: DrillData): string {
  const n = drillDiagrams(d).length;
  return n === 0 ? '' : n === 1 ? ' · 📐' : ` · 📐 ${n}`;
}

/**
 * Show a drill's diagram(s): live geometry when it has some (so it can be
 * watched), otherwise the stored SVG exactly as before. A drill that predates
 * drawn diagrams keeps displaying the picture it has always had.
 *
 * A drill can carry several diagrams (a front against each formation, say):
 * tabs pick which one shows, and full screen has ‹ › to flip through them
 * without closing — that's how it gets used on the field.
 *
 * Shared by the drill library, the shared catalog and the plan's period sheet
 * (run mode). Tapping a diagram opens it full screen with pinch-zoom (the app
 * disables browser zoom); ▶ Watch it run plays the one showing.
 */
export function DrillDiagram({ drill }: { drill: DrillData }) {
  const list = drillDiagrams(drill);
  const [idx, setIdx] = useState(0);
  const [watching, setWatching] = useState(false);
  const [zooming, setZooming] = useState(false);
  // Another drill in the same sheet, or a diagram removed: stay in range.
  useEffect(() => { if (idx >= list.length) setIdx(0); }, [idx, list.length]);
  const cur = list[Math.min(idx, list.length - 1)];
  if (!cur) return null;
  const many = list.length > 1;
  const live = cur.play;
  const canWatch = !!live && live.lines.length > 0;
  const name = (i: number) => diagramTabLabel(list[i]!.label, i);
  const title = many || cur.label ? `${drill.name} — ${name(idx)}` : drill.name;
  return (
    <div style={{ marginTop: 10 }}>
      {many && (
        <div className="diagram-tabs" role="tablist">
          {list.map((g, i) => (
            <button key={i} role="tab" aria-selected={i === idx} className={'chip chip-btn' + (i === idx ? ' on' : '')} onClick={() => setIdx(i)}>
              {name(i)}
            </button>
          ))}
        </div>
      )}
      {!many && cur.label && <div className="muted small">{cur.label}</div>}
      {cur.svg && (
        <div
          className="diagram diagram-zoomable"
          role="button"
          aria-label="Zoom diagram"
          onClick={() => setZooming(true)}
          dangerouslySetInnerHTML={{ __html: sanitizeSvgString(cur.svg) }}
        />
      )}
      {canWatch && (
        <button className="btn btn-primary btn-block" style={{ marginTop: 6 }} onClick={() => setWatching(true)}>▶ Watch it run</button>
      )}
      {cur.svg && zooming && (
        <ZoomView
          svg={sanitizeSvgString(cur.svg)}
          title={title}
          onClose={() => setZooming(false)}
          extra={many ? (
            <span className="zoom-pager">
              <button className="btn btn-sm" aria-label="Previous diagram" disabled={idx === 0} onClick={() => setIdx(idx - 1)}>‹</button>
              <span className="small">{idx + 1}/{list.length}</span>
              <button className="btn btn-sm" aria-label="Next diagram" disabled={idx === list.length - 1} onClick={() => setIdx(idx + 1)}>›</button>
            </span>
          ) : undefined}
        />
      )}
      {live && watching && (
        <Modal open onClose={() => setWatching(false)} size="wide" title={title} actions={<button className="btn" onClick={() => setWatching(false)}>Close</button>}>
          <Suspense fallback={<div className="spinner" />}>
            <PlayAnimator play={live} scratch />
          </Suspense>
        </Modal>
      )}
    </div>
  );
}
